import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Observable } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { ConfirmModalComponent } from './modals/confirm-modal/confirm-modal.component';
import { AgendarConsultaComponent } from './modals/agendar-consulta/agendar-consulta.component';
import { EditarConsultaComponent } from './modals/editar-consulta/editar-consulta.component';
import { ModalEdicaoExamesComponent } from './modals/modal-edicao-exames/modal-edicao-exames.component';
import { ModalLerPdfComponent } from './modals/modal-ler-pdf/modal-ler-pdf.component';
import { ModalAnalisarExameComponent } from './modals/modal-analisar-exame/modal-analisar-exame.component';
import { ModalAnalisarUltrasonografiaComponent } from './modals/modal-analisar-ultrasonografia/modal-analisar-ultrasonografia.component';
import { ModalEdicaoTrimestreComponent } from './modals/modal-edicao-trimestre/modal-edicao-trimestre.component';
import { AlterarEmailComponent } from './modals/alterar-email/alterar-email.component';
import { ExameConfiance, ExameLaboratorio, ExameUltrasom, UltrasonografiaConfiance } from 'app/core/interfaces/pacient-info.interface';
import { Organizacao } from 'app/core/interfaces/organizacao.interface';
import { FormGroup } from '@angular/forms';
import { ModalSearchPacientesComponent } from './modals/modal-search-pacientes/modal-search-pacientes.component';
import { logger } from 'app/core/utils/console';

@Injectable({
    providedIn: 'root'
})
export class ModalsService {
    organizacao: Organizacao = Organizacao.createEmpty();

    constructor(
        private matDialog: MatDialog,
        private authService: AuthService
    ) { }
    
    GetOrgData(): Observable<Organizacao> {
        return Observable.create((observer: any) => {
            this.authService.LerOrgComoObs().subscribe((res) => {
                this.organizacao = res;
                observer.next(res);
            });
        });
    }

    AbrirModalDeConfirmacao(titulo: string, conteudo: string, width: string = '400px', confirmacao: string = 'Continuar', cancelamento = 'Cancelar'): Observable<boolean> {
        return Observable.create((observer: any) => {
            const dialogRef = this.matDialog.open(ConfirmModalComponent, {
                width: width,
                data: {
                    titulo,
                    conteudo,
                    confirmacao,
                    cancelamento
                }
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res ? true : false);
                observer.complete();
            });
        });
    }

    AbrirModalAlterarEmail(email?: string): Observable<string> {
        return Observable.create((observer: any) => {
            const dialogRef = this.matDialog.open(AlterarEmailComponent, {
                width: '400px',
                data: {
                    email: email
                }
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res);
                observer.complete();
            });
        });
    }

    AgendarConsultaModal(data: any, width: string = '800px'): Observable<any> {
        return Observable.create((observer: any) => {
            const dialogRef = this.matDialog.open(AgendarConsultaComponent, {
                width: '800px',
                data
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res);
                observer.complete();
            });
        });
    }

    AbrirPainelConsulta(data: any): Observable<any> {
        return Observable.create((observer: any) => {
            const dialogRef = this.matDialog.open(EditarConsultaComponent, {
                width: '800px',
                data
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res);
                observer.complete();
            });
        });
    }

    ConfirmarDesativarCartao(): Observable<boolean> {
        return this.AbrirModalDeConfirmacao('Deseja desativar o cartão?', 'Uma vez desativado esse cartão não poderá ser ativado novamente! Deseja continuar mesmo assim?');
    }

    ConfirmarSalvarCartaoAlteracoes(): Observable<boolean> {
        return this.AbrirModalDeConfirmacao('Antes de inserir o exame deseja salvar as alterações do cartão?', 'Se incluir um exame sem salvar as alterações, ela será perdida.');
    }

    AbrirModalResultadoExame(exameName: string, oldValue: string): Observable<string> {
        return Observable.create((observer: any) => {
            const dialogRef = this.matDialog.open(ModalEdicaoExamesComponent, {
                width: '500px',
                data: { nameExame: exameName, oldValue }
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res);
                observer.complete();
            });
        });
    }

    AbrirModalDataTrimestre(dataTrimestre?: string): Observable<string> {
        return Observable.create((observer: any) => {
            const dialogRef = this.matDialog.open(ModalEdicaoTrimestreComponent, {
                width: '500px',
                data: {
                    dataInput: dataTrimestre
                }
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res);
                observer.complete();
            });
        });
    }

    async AnalisarExamesPorPDF(exames: ExameLaboratorio[], dum?: string | Date, cartaoId?: string): Promise<{arquivoId: string, examesLaboratorios: ExameLaboratorio[]} | null> {
        return new Promise((resolve) => {
            const lerPdfDialogRef = this.matDialog.open(ModalLerPdfComponent, {
                width: '500px',
                disableClose: true,
                data: { tipo: 'exame' }
            });

            lerPdfDialogRef.afterClosed().subscribe((pdfResult) => {
                if (!pdfResult || !pdfResult.formData) {
                    resolve(null);
                    return;
                }

                const analisarExameDialogRef = this.matDialog.open(ModalAnalisarExameComponent, {
                    width: '900px',
                    maxHeight: '90vh',
                    disableClose: true,
                    data: {
                        formData: pdfResult.formData,
                        cartaoId: cartaoId,
                        initialTrimestre: pdfResult.selectedTrimestre,
                        examesLaboratorios: exames,
                        dum: dum
                    }
                });

                analisarExameDialogRef.afterClosed().subscribe((analyzeResult) => {
                    if (analyzeResult && analyzeResult.action === 'save') {
                        resolve(analyzeResult);
                    } else {
                        resolve(null);
                    }
                });
            });
        });
    }

    async UtilizarArquivoAnalisadoLaboratorial(
        arquivoId: string,
        trimestre: number,
        preAnalisedExams: ExameConfiance[],
        exames: ExameLaboratorio[],
        dum?: string | Date,
        cartaoId?: string,
        dataRealizacao?: string | Date | null
    ): Promise<{arquivoId: string, examesLaboratorios: ExameLaboratorio[]} | null> {
        return new Promise((resolve) => {
            const dialogRef = this.matDialog.open(ModalAnalisarExameComponent, {
                width: '900px',
                maxHeight: '90vh',
                disableClose: true,
                data: {
                    cartaoId: cartaoId,
                    initialTrimestre: trimestre,
                    examesLaboratorios: exames,
                    dum: dum,
                    preAnalisedExams: preAnalisedExams,
                    preAnalisedArquivoId: arquivoId,
                    preAnalisedDataRealizacao: dataRealizacao
                }
            });

            dialogRef.afterClosed().subscribe((analyzeResult) => {
                if (analyzeResult && analyzeResult.action === 'save') {
                    resolve({ ...analyzeResult, arquivoId });
                } else {
                    resolve(null);
                }
            });
        });
    }

    async UtilizarArquivoAnalisadoUSG(
        arquivoId: string,
        preAnalisedUSG: UltrasonografiaConfiance[],
        ultrasonografias: ExameUltrasom[],
        cartaoId?: string
    ): Promise<{arquivoId: string, ultrasonografias: ExameUltrasom[]} | null> {
        return new Promise((resolve) => {
            const dialogRef = this.matDialog.open(ModalAnalisarUltrasonografiaComponent, {
                width: '900px',
                maxHeight: '90vh',
                disableClose: true,
                data: {
                    cartaoId: cartaoId,
                    ultrasonografias: ultrasonografias,
                    preAnalisedUSG: preAnalisedUSG,
                    preAnalisedArquivoId: arquivoId
                }
            });

            dialogRef.afterClosed().subscribe((analyzeResult) => {
                if (analyzeResult && analyzeResult.action === 'save') {
                    resolve({ arquivoId, ultrasonografias: analyzeResult.ultrasonografias });
                } else {
                    resolve(null);
                }
            });
        });
    }

    async AnalisarUltrasonografiaPorPDF(ultrasonografias: ExameUltrasom[], cartaoId?: string): Promise<{arquivoId: string, ultrasonografias: ExameUltrasom[]} | null> {
        return new Promise((resolve) => {
            const lerPdfDialogRef = this.matDialog.open(ModalLerPdfComponent, {
                width: '500px',
                disableClose: true
            });

            lerPdfDialogRef.afterClosed().subscribe((pdfResult) => {
                if (!pdfResult || !pdfResult.formData) {
                    resolve(null);
                    return;
                }

                logger.log('Dados extraídos do PDF de ultrassonografia:', pdfResult.formData);

                const analisarUltrasonografiaDialogRef = this.matDialog.open(ModalAnalisarUltrasonografiaComponent, {
                    width: '900px',
                    maxHeight: '90vh',
                    disableClose: true,
                    data: {
                        formData: pdfResult.formData,
                        cartaoId: cartaoId,
                        ultrasonografias
                    }
                });

                analisarUltrasonografiaDialogRef.afterClosed().subscribe((analyzeResult) => {
                    if (analyzeResult && analyzeResult.action === 'save') {
                        resolve(analyzeResult);
                    } else {
                        resolve(null);
                    }
                });
            });
        });
    }

    AbrirModalSearchPacientes(stateForm: FormSearchPacientes): Observable<any> {
        return new Observable<any>((observer) => {
            const dialogRef = this.matDialog.open(ModalSearchPacientesComponent, {
                width: '570px',
                data: {
                    stateForm
                }
            });

            dialogRef.afterClosed().subscribe((res) => {
                observer.next(res);
                observer.complete();
            });
        });
    }  
}
